import moment from 'moment';
import { z } from 'zod';

import type { SchoolHolidayTerm } from '../../../../legacy/types';
import { REVISION_PERIOD_DAYS, WEEK_BEFORE_TERM_START_DAYS } from './constants';

export const createPauseFormValidationSchema = (nextSchoolTerm: SchoolHolidayTerm) =>
  z.lazy(() =>
    z
      .object({
        pauseStartDate: z
          .string()
          .datetime('Invalid date')
          .nullable()
          .refine((val) => val !== null, { message: 'Start date is required.' }),
        resumptionDate: z.string().datetime('Invalid date').nullable(),
        pauseByWeeksValue: z.string().optional(),
        pauseMethod: z.string().optional(),
        pauseResumptionMethod: z.string().optional(),
      })
      .superRefine((fields, ctx) => {
        const { pauseStartDate, resumptionDate, pauseByWeeksValue, pauseMethod, pauseResumptionMethod } = fields;
        const startDate = moment(pauseStartDate);
        const endDate = moment(resumptionDate);

        if (
          !!pauseStartDate &&
          !!resumptionDate &&
          startDate.isSameOrAfter(endDate) &&
          pauseMethod !== 'pause-by-weeks'
        ) {
          const message = `Resumption date must be after the pause start date ${startDate.format('DD MMMM YYYY')}`;
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path: !!pauseResumptionMethod ? ['resumptionDate', pauseResumptionMethod] : ['resumptionDate'],
          });
        }

        if (resumptionDate === null && pauseMethod !== 'pause-by-weeks') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Resumption date is required.',
            path: !!pauseResumptionMethod ? ['resumptionDate', pauseResumptionMethod] : ['resumptionDate'],
          });
        } else if (resumptionDate === null && pauseMethod === 'pause-by-weeks' && !pauseByWeeksValue) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please select how many weeks to pause.',
            path: !!pauseMethod ? ['pauseStartDate', pauseMethod] : ['pauseStartDate'],
          });
        }
        const today = moment();
        const weekBeforeTermStart = moment(nextSchoolTerm?.startDate).subtract(WEEK_BEFORE_TERM_START_DAYS, 'days');
        const revisionPeriod = moment(nextSchoolTerm?.startDate).subtract(REVISION_PERIOD_DAYS, 'days');
        const todayIsAfterAWeekBeforeTermStart = today.isAfter(weekBeforeTermStart);
        const todayIsAfterRevisionPeriod = today.isAfter(revisionPeriod);

        if (todayIsAfterAWeekBeforeTermStart && pauseResumptionMethod === 'resume-a-week-before-term-start') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Selected date has already passed, please select a different resumption date.',
            path: ['resumptionDate', pauseResumptionMethod],
          });
        }
        if (todayIsAfterRevisionPeriod && pauseResumptionMethod === 'resume-on-revision-period') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Selected date has already passed, please select a different resumption date.',
            path: ['resumptionDate', pauseResumptionMethod],
          });
        }
      })
  );

export type PauseFormValidationSchemaType = z.infer<ReturnType<typeof createPauseFormValidationSchema>>;

export type ValidateFormResult = {
  success: boolean;
  data?: PauseFormValidationSchemaType;
  error?: z.ZodFormattedError<PauseFormValidationSchemaType>;
};

export const validateForm = (
  {
    pauseStartDate,
    resumptionDate,
    pauseByWeeksValue,
    pauseMethod,
    pauseResumptionMethod,
  }: PauseFormValidationSchemaType,
  nextSchoolTerm: SchoolHolidayTerm
): ValidateFormResult => {
  const result = createPauseFormValidationSchema(nextSchoolTerm).safeParse({
    pauseStartDate,
    resumptionDate,
    pauseByWeeksValue,
    pauseMethod,
    pauseResumptionMethod,
    nextSchoolTerm,
  });

  if (!result.success) {
    const error = (result as z.SafeParseError<{ pauseStartDate: string; resumptionDate: string }>).error;

    return {
      success: false as const,
      error: error.format(),
    };
  } else {
    return {
      success: true as const,
      data: result.data,
      error: undefined,
    };
  }
};
